import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { FaPaperPlane, FaTimes, FaTrash } from "react-icons/fa";
import {
  buildCreateInquiryBatchPayload,
  buildDefaultInquiryBatchName,
  groupSelectedByProvider,
} from "./inquiryBatchUtils";
import {
  createInquiryBatch,
  previewInquiryBatchCreate,
} from "./inquiryBatchApi";
import {
  getInquiryFieldsFromApplication,
  getMissingInquiryBatchFields,
} from "./inquiryApplicationUtils";
import {
  confirmRemoveInquiryBatchItem,
  confirmRemoveInquiryBatchItems,
} from "./inquiryBatchItemDeleteHelpers";

const SKIPPED_ACTION_BTN =
  "inline-flex h-7 w-7 items-center justify-center rounded-md border text-[12px] transition focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50";

function skippedRowKey(row, index) {
  const batchId = row?.existing_batch?.id ?? "";
  const itemId = row?.existing_item?.id ?? "";
  return `${batchId}:${itemId}:${index}`;
}

const inputClass =
  "h-9 w-full rounded border border-slate-200 bg-white px-2.5 text-[13px] font-medium text-slate-800 outline-none transition focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20";

function CreateInquiryBatchModal({
  open,
  applications = [],
  applicationType = "education",
  providerLabel = "",
  onClose,
  onCreated,
}) {
  const [batchName, setBatchName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState({
    eligible_count: 0,
    skipped_count: 0,
    skipped: [],
  });
  const [skippedSelectedKeys, setSkippedSelectedKeys] = useState(
    () => new Set(),
  );
  const [skippedDeleting, setSkippedDeleting] = useState(false);

  const providerGroups = useMemo(
    () => groupSelectedByProvider(applications),
    [applications],
  );

  const multipleProviders = providerGroups.length > 1;
  const singleProviderLabel =
    providerLabel ||
    (providerGroups.length === 1 ? providerGroups[0].label : "");

  const suggestedBatchName = useMemo(() => {
    return buildDefaultInquiryBatchName({ providerLabel: singleProviderLabel });
  }, [singleProviderLabel]);

  const incompleteCount = useMemo(() => {
    return applications.filter((app) => {
      const fields = getInquiryFieldsFromApplication(app);
      return getMissingInquiryBatchFields(fields).length > 0;
    }).length;
  }, [applications]);

  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setError("");
    setSkippedSelectedKeys(new Set());
    setBatchName(buildDefaultInquiryBatchName({ providerLabel: singleProviderLabel }));
  }, [open, applications, singleProviderLabel]);

  const loadPreview = useCallback(async () => {
    if (!open || multipleProviders || applications.length === 0) {
      setPreview({ eligible_count: 0, skipped_count: 0, skipped: [] });
      return;
    }

    setPreviewLoading(true);
    setError("");

    const payload = buildCreateInquiryBatchPayload({
      applications,
      batchName: "preview",
      markSent: false,
      applicationType,
    });

    try {
      const data = await previewInquiryBatchCreate(payload);
      setPreview(data);
    } catch (err) {
      setPreview({
        eligible_count: 0,
        skipped_count: err?.skipped?.length ?? 0,
        skipped: err?.skipped ?? [],
      });
      setError(err?.message || "Failed to load preview.");
    } finally {
      setPreviewLoading(false);
    }
  }, [open, applications, applicationType, multipleProviders]);

  useEffect(() => {
    if (!open) return;
    loadPreview();
  }, [open, loadPreview]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && !submitting) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, submitting, onClose]);

  if (!open) return null;

  const skippedRows = preview.skipped || [];
  const skippedSelectable = skippedRows.filter(
    (row) => row?.existing_batch?.id && row?.existing_item?.id,
  );

  const toggleSkippedRow = (key) => {
    setSkippedSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllSkipped = () => {
    const keys = skippedRows
      .map((row, index) =>
        row?.existing_batch?.id && row?.existing_item?.id
          ? skippedRowKey(row, index)
          : null,
      )
      .filter(Boolean);
    const allSelected =
      keys.length > 0 && keys.every((key) => skippedSelectedKeys.has(key));
    setSkippedSelectedKeys(allSelected ? new Set() : new Set(keys));
  };

  const handleDeleteSkippedRow = async (row) => {
    const batchId = row?.existing_batch?.id;
    const itemId = row?.existing_item?.id;
    if (!batchId || !itemId || skippedDeleting) return;

    setSkippedDeleting(true);
    try {
      const removed = await confirmRemoveInquiryBatchItem(
        { id: itemId, student_name: row.student_name },
        batchId,
      );
      if (removed) {
        setSkippedSelectedKeys(new Set());
        await loadPreview();
      }
    } catch (err) {
      setError(err?.message || "Failed to remove student from batch.");
    } finally {
      setSkippedDeleting(false);
    }
  };

  const handleDeleteSkippedSelected = async () => {
    if (skippedDeleting || skippedSelectedKeys.size === 0) return;

    const selectedRows = skippedRows
      .map((row, index) => ({ row, key: skippedRowKey(row, index) }))
      .filter(({ key, row }) => skippedSelectedKeys.has(key) && row.existing_batch?.id && row.existing_item?.id)
      .map(({ row }) => ({
        batchId: row.existing_batch.id,
        itemId: row.existing_item.id,
        student_name: row.student_name,
      }));

    if (!selectedRows.length) return;

    setSkippedDeleting(true);
    try {
      const removed = await confirmRemoveInquiryBatchItems(selectedRows);
      if (removed) {
        setSkippedSelectedKeys(new Set());
        await loadPreview();
      }
    } catch (err) {
      setError(err?.message || "Failed to remove selected students.");
    } finally {
      setSkippedDeleting(false);
    }
  };

  const allSkippedChecked =
    skippedSelectable.length > 0 &&
    skippedRows.every((row, index) => {
      if (!row?.existing_batch?.id || !row?.existing_item?.id) return true;
      return skippedSelectedKeys.has(skippedRowKey(row, index));
    });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting || multipleProviders || preview.eligible_count === 0) return;

    setError("");
    setSubmitting(true);
    try {
      const payload = buildCreateInquiryBatchPayload({
        applications,
        batchName,
        markSent: false,
        applicationType,
      });
      const result = await createInquiryBatch(payload);
      onCreated?.(result);
    } catch (err) {
      setError(err?.message || "Failed to create inquiry batch.");
      if (err?.skipped?.length) {
        setPreview((prev) => ({
          ...prev,
          skipped: err.skipped,
          skipped_count: err.skipped.length,
        }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const subtitleProvider = singleProviderLabel || "selected provider";

  return createPortal(
    <>
      <div
        className="cms-app-modal-backdrop"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
        onMouseDown={() => {
          if (!submitting && !skippedDeleting) onClose?.();
        }}
        aria-hidden="true"
      />
      <div
        className="cms-app-modal cms-app-modal--centered pointer-events-none flex items-center justify-center p-3 sm:p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-inquiry-batch-title"
      >
        <div
          className="tailwind-container pointer-events-auto relative z-10 flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl border border-slate-200 bg-white shadow-xl"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
            <div className="min-w-0">
              <h2
                id="create-inquiry-batch-title"
                className="text-[15px] font-semibold text-slate-900"
              >
                Create inquiry batch
              </h2>
              <p className="mt-0.5 text-[12px] text-slate-600">
                {previewLoading
                  ? "Checking students…"
                  : `Creating batch for ${subtitleProvider} with ${preview.eligible_count} student${preview.eligible_count === 1 ? "" : "s"}${
                      preview.skipped_count > 0
                        ? ` (${preview.skipped_count} already in inquiry — see below)`
                        : ""
                    }.`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => !submitting && !skippedDeleting && onClose?.()}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50"
              style={{ boxShadow: "none" }}
            >
              <FaTimes size={12} />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
          >
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
              {multipleProviders ? (
                <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] font-medium text-amber-900">
                  Inquiry batches are per provider. Filter by one provider or
                  adjust your selection.
                </p>
              ) : null}

              {incompleteCount > 0 ? (
                <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-600">
                  {incompleteCount} selected student
                  {incompleteCount === 1 ? "" : "s"} missing inquiry fields.
                  You can still create the batch; complete details before
                  sending email to the provider.
                </p>
              ) : null}

              <div>
                <label
                  htmlFor="inquiry-batch-name"
                  className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500"
                >
                  Batch name <span className="text-rose-500">*</span>
                </label>
                <input
                  id="inquiry-batch-name"
                  type="text"
                  value={batchName}
                  onChange={(e) => setBatchName(e.target.value)}
                  className={inputClass}
                  disabled={submitting || multipleProviders}
                  required
                />
              </div>

              {preview.skipped_count > 0 ? (
                <div>
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Already in an inquiry ({preview.skipped_count})
                    </p>
                    {skippedSelectedKeys.size > 0 ? (
                      <button
                        type="button"
                        disabled={skippedDeleting || submitting}
                        onClick={handleDeleteSkippedSelected}
                        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 text-[12px] font-semibold text-red-600 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                        style={{ boxShadow: "none" }}
                      >
                        Delete selected ({skippedSelectedKeys.size})
                      </button>
                    ) : null}
                  </div>
                  <div className="max-h-48 overflow-auto rounded-md border border-slate-200">
                    <table className="w-full text-left text-[12px]">
                      <thead className="sticky top-0 bg-slate-50 text-[10px] font-semibold uppercase text-slate-500">
                        <tr>
                          <th className="w-8 px-2 py-1.5">
                            <input
                              type="checkbox"
                              checked={allSkippedChecked}
                              disabled={
                                skippedSelectable.length === 0 ||
                                skippedDeleting
                              }
                              onChange={toggleAllSkipped}
                              aria-label="Select all students with existing inquiry"
                            />
                          </th>
                          <th className="px-2 py-1.5">Student</th>
                          <th className="px-2 py-1.5">Batch</th>
                          <th className="w-12 px-2 py-1.5 text-right">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {skippedRows.map((row, index) => {
                          const key = skippedRowKey(row, index);
                          const canDelete =
                            row.existing_batch?.id && row.existing_item?.id;
                          return (
                            <tr key={key}>
                              <td className="px-2 py-1.5">
                                <input
                                  type="checkbox"
                                  checked={skippedSelectedKeys.has(key)}
                                  disabled={!canDelete || skippedDeleting}
                                  onChange={() => toggleSkippedRow(key)}
                                  aria-label={`Select ${row.student_name || "student"}`}
                                />
                              </td>
                              <td className="px-2 py-1.5 font-medium text-slate-800">
                                {row.student_name || "—"}
                              </td>
                              <td className="px-2 py-1.5">
                                {row.existing_batch?.id ? (
                                  <Link
                                    to={`/commissions/inquiry-batches/${row.existing_batch.id}`}
                                    className="font-semibold text-[#007BFF] hover:underline"
                                    onClick={() => onClose?.()}
                                  >
                                    {row.existing_batch.batch_no ||
                                      row.existing_batch.batch_name ||
                                      `Batch #${row.existing_batch.id}`}
                                  </Link>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-right">
                                {canDelete ? (
                                  <button
                                    type="button"
                                    disabled={skippedDeleting || submitting}
                                    onClick={() => handleDeleteSkippedRow(row)}
                                    title="Remove from batch"
                                    aria-label="Remove from batch"
                                    className={`${SKIPPED_ACTION_BTN} border-red-200 bg-red-50 text-red-600 hover:bg-red-100 focus:ring-red-200`}
                                    style={{ boxShadow: "none" }}
                                  >
                                    <FaTrash size={12} />
                                  </button>
                                ) : null}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {error ? (
                <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] font-medium text-rose-700">
                  {error}
                </p>
              ) : null}
            </div>

            <div className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-100 px-4 py-3">
              <button
                type="button"
                onClick={() => !submitting && !skippedDeleting && onClose?.()}
                disabled={submitting || skippedDeleting}
                className="inline-flex h-9 items-center rounded-md border border-slate-200 bg-white px-3 text-[13px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                style={{ boxShadow: "none" }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  submitting ||
                  skippedDeleting ||
                  previewLoading ||
                  multipleProviders ||
                  preview.eligible_count === 0 ||
                  !String(batchName || "").trim()
                }
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#007BFF] px-3 text-[13px] font-semibold text-white hover:bg-[#0069D9] disabled:cursor-not-allowed disabled:opacity-60"
                style={{ border: "none", boxShadow: "none" }}
              >
                <FaPaperPlane size={12} />
                {submitting ? "Creating…" : "Create inquiry batch"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body,
  );
}

export default CreateInquiryBatchModal;
